import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React from 'react';

import {DataRange} from 'app/components/AnalyzePolygonChart/types';
import colors from 'app/utils/colorUtils';
import {GradientStops} from 'app/utils/gradientStops';
import * as layers from 'app/utils/layers';

import cs from './styles.module.scss';

interface ComboSliderProps {
  onChangeValue: (thresholdValue: number) => void;
  value: number;
  min;
  max;
  labels: [string, string];
  gradientStops?: GradientStops;
  step?: number;
  disabled?: boolean;
}

interface ThresholdSliderProps {
  layer: layers.DataLayerInfo;
  onChangeValue: (thresholdValue: number) => void;
  thresholdValue: number;
  disabled?: boolean;
}

interface RangeSliderProps {
  layer: layers.DataLayerInfo;
  min: number | null;
  max: number | null;
  onChangeRange: (bounds: {min: number | null; max: number | null}) => void;
  disabled?: boolean;
  intent?: B.Intent;
  // Distinguishes one slider's controls from another's when several sit on one screen.
  boundLabelContext?: string;
}

const DEFAULT_GRADIENT_STOPS: GradientStops = [
  [0, colors.brightBlue],
  [1, colors.lightGray],
];

const getDefaultStep = (min: number, max: number) => {
  // Calculate the order of magnitude of the range
  const range = max - min;
  const orderOfMagnitude = Math.floor(Math.log10(range));
  // Calculate the step as a power of ten based on the order of magnitude
  let step = Math.pow(10, orderOfMagnitude - 1);

  // Adjust step by 1 level if it's too large or too small based on the range
  if (range / step > 100) {
    step *= 10;
  } else if (range / step < 10) {
    step /= 10;
  }
  return step;
};

// 0.1 isn't binary-exact, so Math.round(raw / step) * step lands on 1.2000000000000002.
const roundToStep = (value: number, step: number) =>
  Number((Math.round(value / step) * step).toFixed(10));

// Appends the numeric value to both bound labels if EITHER fails to spell its value out (avoids a
// pair like "None (0)" / "High Vigor" where only one side got the parenthetical, which reads as
// though the two ends were measured differently). Whole pair stays bare only when both match.
const formatBoundLabels = (
  labels: layers.DataLabels | undefined,
  lowValue: number,
  highValue: number
): [string, string] => {
  if (!labels) return [String(lowValue), String(highValue)];
  const [lowLabel, highLabel] = labels;
  const matchesValue = (label: string, value: number) =>
    label.match(/[0-9]+/)?.toString() === value.toString();
  if (!matchesValue(lowLabel, lowValue) || !matchesValue(highLabel, highValue)) {
    return [`${lowLabel} (${lowValue})`, `${highLabel} (${highValue})`];
  }
  return [lowLabel, highLabel];
};

/** A custom slider built on range and number html inputs */
export const ComboSlider = ({
  min,
  max,
  onChangeValue,
  value,
  labels,
  gradientStops = DEFAULT_GRADIENT_STOPS,
  step = getDefaultStep(min, max),
  disabled,
}: ComboSliderProps) => {
  return (
    <div className={cs.sliderContainer}>
      <div className={cs.comboSlider}>
        <B.NumericInput
          value={value}
          min={min}
          max={max}
          buttonPosition="none"
          disabled={disabled}
          onValueChange={(valueAsNumber) => {
            // Clamp value to range
            // Handles the case where a user types a value into the input that is outside the range
            // Also handles NaN/invalid values by keeping the current value
            if (!Number.isFinite(valueAsNumber)) {
              return;
            }
            const newValue = Math.min(Math.max(valueAsNumber, min), max);
            onChangeValue(newValue);
          }}
          step={step}
        />
        <input
          //inline styles so we can dynamically generate the gradient background for our slider
          style={{
            background: `linear-gradient(to left, ${gradientStops.map((stop) => stop[1], stop[0])})`,
            width: '100%',
          }}
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(ev) => {
            onChangeValue(Number(ev.currentTarget.value));
          }}
          step={step}
          disabled={disabled}
        ></input>
        <div className={cs.sliderLabels}>
          {labels.map((label) => (
            <div key={label}>{label}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

/** A custom slider built on range and number html inputs so we can render a gradient as the slider background */
export const ThresholdSlider = ({
  layer,
  onChangeValue,
  thresholdValue,
  disabled,
}: ThresholdSliderProps) => {
  const [dataRangeMin, dataRangeMax]: DataRange = layer.dataRange;
  const labels: layers.DataLabels | undefined = layer.dataLabels;

  const formattedLabels: [string, string] = React.useMemo(
    () => formatBoundLabels(labels, dataRangeMin, dataRangeMax),
    [labels, dataRangeMin, dataRangeMax]
  );

  const gradientStops: readonly (readonly [number, string])[] | (string | number)[][] =
    React.useMemo(() => {
      // the colors for Planet Forest Carbon would be misleading here (because the colors represent
      // relative values on the map, so the dataMax doesn't always correspond to the gradient's max)
      // so using a solid blue line.
      // TODO(eva) if more layers start having a case like this where the gradient would be misleading
      // in this context, we should probably store that info with the layerInfo.
      return layer.key === layers.PLANET_FOREST_ABOVEGROUND_CARBON
        ? [
            [0, colors.brightBlue],
            [100, colors.brightBlue],
          ]
        : layer.gradientStops;
    }, [layer]);

  return (
    <ComboSlider
      labels={formattedLabels}
      min={dataRangeMin}
      max={dataRangeMax}
      onChangeValue={onChangeValue}
      value={thresholdValue}
      gradientStops={gradientStops}
      disabled={disabled}
    />
  );
};

// GradientStops are authored offset-descending by value: offset 0 is the color for the TOP of a
// vertical legend, which is the high end of the range, not the low end (see
// colorUtils.graphScaleFromGradientStops, which documents and corrects for the same thing when
// building chart color scales). Reversing + inverting the offset gets colors in ascending-value
// order, matching a left-to-right track.
const gradientStopsAscendingByValue = (stops: GradientStops) =>
  [...stops].map(([offset, color]) => ({percent: (1 - offset) * 100, color})).reverse();

/**
 * A focusable, keyboard- and pointer-operable slider handle for one bound of a RangeSlider.
 * Left as a plain positioned div (rather than a native input) so it can sit inside the shared
 * track and respond to drags that start from either handle or the track itself.
 */
const RangeHandle = ({
  label,
  percent,
  min,
  max,
  value,
  step,
  danger,
  ghost,
  disabled,
  onChange,
}: {
  label: string;
  percent: number;
  min: number;
  max: number;
  value: number;
  step: number;
  danger: boolean;
  // A bound the user hasn't set yet: parked at the span edge so the drag has somewhere to start.
  ghost?: boolean;
  disabled?: boolean;
  onChange: (value: number) => void;
}) => {
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    const track = event.currentTarget.parentElement;
    if (!track) return;
    event.currentTarget.setPointerCapture(event.pointerId);

    const onMove = (moveEvent: PointerEvent) => {
      onChange(valueForClientX(track, moveEvent.clientX, min, max, step));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        onChange(roundToStep(value + step, step));
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        onChange(roundToStep(value - step, step));
        break;
      case 'Home':
        event.preventDefault();
        onChange(min);
        break;
      case 'End':
        event.preventDefault();
        onChange(max);
        break;
    }
  };

  return (
    <div
      className={classnames(cs.handle, danger && cs.handleDanger, ghost && cs.handleGhost)}
      // The true percent, not travel inset by the handle radius: the band edges, scrims and axis
      // all sit at this position, and valueForClientX maps the pointer across the full track. An
      // inset would put the circle up to half a radius off from the bound it marks.
      style={{left: `${percent}%`}}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={ghost ? 'Not set' : undefined}
      aria-disabled={disabled}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    />
  );
};

const percentForValue = (value: number, min: number, max: number) =>
  ((value - min) / (max - min)) * 100;

const valueForClientX = (
  track: Element,
  clientX: number,
  min: number,
  max: number,
  step: number
) => {
  const rect = track.getBoundingClientRect();
  const rawPercent = ((clientX - rect.left) / rect.width) * 100;
  const percent = Math.min(Math.max(rawPercent, 0), 100);
  const raw = min + (percent / 100) * (max - min);
  const snapped = roundToStep(raw, step);
  return Math.min(Math.max(snapped, min), max);
};

/**
 * Two bounds on one layer, either of which may be left blank for the one-sided case, set on a
 * gradient track rather than blind number boxes.
 *
 * Spans evaluableRange, then rawDataRange, where the layer has one: dataRange is only the coloring
 * range, and the alert worker compares raw index values with no rescale, so a bound outside the
 * colored band is evaluable even though the map can't shade it. The colored band is drawn at
 * dataRange within that wider span so the user can see where the colormap actually stops.
 */
export const RangeSlider = ({
  layer,
  min,
  max,
  onChangeRange,
  disabled,
  intent,
  boundLabelContext,
}: RangeSliderProps) => {
  const [spanMin, spanMax]: DataRange =
    layer.evaluableRange ?? layer.rawDataRange ?? layer.dataRange;
  const [dataMin, dataMax]: DataRange = layer.dataRange;
  const danger = intent === B.Intent.DANGER;
  const labelContext = boundLabelContext ?? layer.shortName;

  // Stepped over dataRange, not the span. evaluableRange widens the span to a normalized index's
  // full math range, which would coarsen the step tenfold on a layer like NDWI (dataRange
  // [-0.1, 0.4] gives 0.01; the [-1, 1] span gives 0.1) and put a bound like 0.15 out of reach.
  const step = React.useMemo(() => getDefaultStep(dataMin, dataMax), [dataMin, dataMax]);

  const trackRef = React.useRef<HTMLDivElement>(null);

  const loPercent = min === null ? 0 : percentForValue(min, spanMin, spanMax);
  const hiPercent = max === null ? 100 : percentForValue(max, spanMin, spanMax);
  const dataMinPercent = percentForValue(dataMin, spanMin, spanMax);
  const dataMaxPercent = percentForValue(dataMax, spanMin, spanMax);
  const spanWiderThanDataRange = dataMinPercent > 0 || dataMaxPercent < 100;

  const gradientCss = React.useMemo(() => {
    const stops = gradientStopsAscendingByValue(layer.gradientStops);
    return `linear-gradient(to right, ${stops
      .map(({percent, color}) => `${color} ${percent}%`)
      .join(', ')})`;
  }, [layer.gradientStops]);

  const setMin = (value: number) => {
    if (disabled) return;
    onChangeRange({min: Math.min(value, max ?? spanMax), max});
  };
  const setMax = (value: number) => {
    if (disabled) return;
    onChangeRange({min, max: Math.max(value, min ?? spanMin)});
  };

  const onTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    const value = valueForClientX(event.currentTarget, event.clientX, spanMin, spanMax, step);
    const distanceToMin = Math.abs(value - (min ?? spanMin));
    const distanceToMax = Math.abs(value - (max ?? spanMax));
    if (distanceToMin <= distanceToMax) setMin(value);
    else setMax(value);
  };

  const [lowLabel, highLabel] = formatBoundLabels(layer.dataLabels, dataMin, dataMax);

  return (
    <div className={cs.rangeSlider}>
      <div className={cs.rangeSliderTrackArea}>
        <div
          className={cs.track}
          ref={trackRef}
          onPointerDown={onTrackPointerDown}
          aria-hidden={disabled}
        >
          <div
            className={cs.trackColorBand}
            style={{
              left: `${dataMinPercent}%`,
              width: `${dataMaxPercent - dataMinPercent}%`,
              background: gradientCss,
            }}
          />
          <div className={cs.scrim} style={{left: 0, width: `${loPercent}%`}} />
          <div className={cs.scrim} style={{left: `${hiPercent}%`, width: `${100 - hiPercent}%`}} />
          {min !== null && (
            <div
              className={classnames(cs.bandEdge, danger && cs.bandEdgeDanger)}
              style={{left: `${loPercent}%`}}
            />
          )}
          {max !== null && (
            <div
              className={classnames(cs.bandEdge, danger && cs.bandEdgeDanger)}
              style={{left: `${hiPercent}%`}}
            />
          )}
          {min === null && (
            <div className={cs.openArrowLeft} aria-hidden>
              ‹
            </div>
          )}
          {max === null && (
            <div className={cs.openArrowRight} aria-hidden>
              ›
            </div>
          )}
          {/* An unset bound still gets a handle, parked at the span edge. Without one a pristine
              range is a bare gradient with nothing to grab, leaving the number inputs as the only
              discoverable way in. */}
          <RangeHandle
            label={`${labelContext} lower bound`}
            percent={loPercent}
            min={spanMin}
            max={max ?? spanMax}
            value={min ?? spanMin}
            step={step}
            danger={danger}
            ghost={min === null}
            disabled={disabled}
            onChange={setMin}
          />
          <RangeHandle
            label={`${labelContext} upper bound`}
            percent={hiPercent}
            min={min ?? spanMin}
            max={spanMax}
            value={max ?? spanMax}
            step={step}
            danger={danger}
            ghost={max === null}
            disabled={disabled}
            onChange={setMax}
          />
        </div>
        <div className={cs.axis}>
          {spanWiderThanDataRange && (
            <span className={cs.tick} style={{left: `${dataMinPercent}%`}} />
          )}
          {spanWiderThanDataRange && (
            <span className={cs.tick} style={{left: `${dataMaxPercent}%`}} />
          )}
          <span className={cs.axisLabelStart} style={{left: `${dataMinPercent}%`}}>
            {lowLabel}
          </span>
          <span className={cs.axisLabelEnd} style={{left: `${dataMaxPercent}%`}}>
            {highLabel}
          </span>
        </div>
      </div>
      <div className={cs.rangeSliderInputs}>
        {/* An empty box means no limit on that end, so the placeholder says that rather than
            printing the span endpoint in maths notation. */}
        <BoundInput
          bound={min}
          placeholder="No minimum"
          ariaLabel={`${labelContext} minimum`}
          rangeMin={spanMin}
          rangeMax={spanMax}
          disabled={disabled}
          intent={intent}
          onChangeBound={(value) => onChangeRange({min: value, max})}
        />
        <span className={cs.rangeSliderSeparator}>to</span>
        <BoundInput
          bound={max}
          placeholder="No maximum"
          ariaLabel={`${labelContext} maximum`}
          rangeMin={spanMin}
          rangeMax={spanMax}
          disabled={disabled}
          intent={intent}
          onChangeBound={(value) => onChangeRange({min, max: value})}
        />
        {layer.dataUnit && <span className={cs.rangeSliderUnit}>{layer.dataUnit}</span>}
      </div>
    </div>
  );
};

// '' clears the bound; anything that isn't a number yet ('-', '0.') is still being typed.
const parseBound = (text: string): number | null | undefined => {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * One bound of a range.
 *
 * Holds what was typed rather than rendering the parsed number back, so a leading '-' or a trailing
 * '.' survives long enough to finish typing, and clamps to the layer's range on blur rather than
 * mid-value. A plain input rather than NumericInput for the same reason: NumericInput rejects an
 * in-progress value as malformed, and its stepper precision would round a typed decimal.
 */
const BoundInput = ({
  bound,
  placeholder,
  ariaLabel,
  rangeMin,
  rangeMax,
  disabled,
  intent,
  onChangeBound,
}: {
  bound: number | null;
  placeholder: string;
  ariaLabel: string;
  rangeMin: number;
  rangeMax: number;
  disabled?: boolean;
  intent?: B.Intent;
  onChangeBound: (value: number | null) => void;
}) => {
  const [draft, setDraft] = React.useState(bound === null ? '' : String(bound));

  // Resync when the bound changes from outside, as picking a new layer rebuilds the condition.
  React.useEffect(() => {
    setDraft((current) =>
      parseBound(current) === bound ? current : bound === null ? '' : String(bound)
    );
  }, [bound]);

  return (
    <B.InputGroup
      value={draft}
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      intent={intent}
      inputMode="decimal"
      onValueChange={(value) => {
        setDraft(value);
        const parsed = parseBound(value);
        if (parsed !== undefined) onChangeBound(parsed);
      }}
      onBlur={() => {
        const parsed = parseBound(draft);
        if (parsed === undefined) {
          setDraft(bound === null ? '' : String(bound));
          return;
        }
        if (parsed === null) return setDraft('');
        const clamped = Math.min(Math.max(parsed, rangeMin), rangeMax);
        setDraft(String(clamped));
        if (clamped !== bound) onChangeBound(clamped);
      }}
    />
  );
};
